import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SupabaseIcon } from '@/components/SupabaseIcon';
import WorkshopStepWrapper from '../WorkshopStepWrapper';
import { useWorkshopProgress } from '@/contexts/WorkshopProgressContext';
import { Edit3, Target, Trophy, CheckCircle } from 'lucide-react';

export const WorkshopLanding: React.FC = () => {
  const { navigateToStep, dispatch } = useWorkshopProgress();

  const handleStartWorkshop = () => {
    dispatch({ type: 'START_WORKSHOP' });
  };

  return (
    <WorkshopStepWrapper
      step="landing"
      title="5-Minute AI Quality Workshop"
      description="Join Lyra for a hands-on workshop where you'll actively improve real AI content"
      showNavigation={false}
      showStepInfo={false}
      showProgress={false}
    >
      {/* Hero Section */}
      <div className="text-center mb-16">
        <div className="w-32 h-32 mx-auto mb-8 bg-white rounded-3xl shadow-lg flex items-center justify-center">
          <SupabaseIcon
            iconPath="lyra-avatar.png"
            alt="Lyra AI Mentor"
            size="xl"
            className="w-20 h-20"
          />
        </div>

        <div className="inline-block bg-purple-600 text-white px-6 py-3 rounded-full text-sm font-semibold mb-8 shadow-lg">
          LIVE AI QUALITY WORKSHOP
        </div>

        <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8 leading-tight">
          5-Minute AI Quality Workshop
        </h1>

        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          Join Lyra for a hands-on workshop where you'll actively improve real AI content.
          Experience the transformation from generic AI output to mission-aligned, professional communication
          that drives real results for nonprofit and public interest work.
        </p>

        {/* Lyra's Introduction */}
        <div className="bg-white rounded-3xl shadow-lg p-8 mb-12 max-w-3xl mx-auto">
          <div className="flex items-start space-x-6">
            <div className="w-16 h-16 rounded-2xl bg-purple-100 flex items-center justify-center">
              <SupabaseIcon
                iconPath="lyra-avatar.png"
                alt="Lyra"
                size="lg"
                className="w-12 h-12"
              />
            </div>
            <div className="flex-1 text-left">
              <h2 className="text-2xl font-bold text-purple-800 mb-3">Hi! I'm Lyra, Your AI Coach</h2>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                "I'll guide you through fixing a real AI output that's plagued by generic language and missed opportunities.
                You'll actively practice the techniques that transform AI from basic to breakthrough - and see exactly
                what professional AI mastery looks like in action."
              </p>
              <div className="bg-purple-50 border border-purple-200 p-4 rounded-xl">
                <div className="text-purple-800 font-semibold">Your 5-Minute Journey:</div>
                <div className="text-purple-700 mt-2">Challenge → Practice → Transform → Master → Advance</div>
              </div>
            </div>
          </div>
        </div>

        <Button
          size="lg"
          className="bg-purple-600 hover:bg-purple-700 text-white px-10 py-4 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
          onClick={handleStartWorkshop}
        >
          <Edit3 className="w-5 h-5 mr-2" />
          Start Live Workshop
        </Button>
      </div>

      {/* Workshop Preview */}
      <div className="grid md:grid-cols-3 gap-8 mb-20">
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
            <SupabaseIcon
              iconPath="communication.png"
              alt="Interactive Challenge"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Interactive Challenge</h3>
          <p className="text-gray-600 leading-relaxed">
            Hands-on practice with real AI content that needs improvement
          </p>
        </div>
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
            <SupabaseIcon
              iconPath="achievement-trophy.png"
              alt="Real-Time Coaching"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Real-Time Coaching</h3>
          <p className="text-gray-600 leading-relaxed">
            Lyra guides you through each improvement with expert feedback
          </p>
        </div>
        <div className="text-center">
          <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-6 flex items-center justify-center">
            <SupabaseIcon
              iconPath="data-analytics.png"
              alt="Skill Validation"
              size="lg"
              className="w-12 h-12"
            />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-4">Skill Validation</h3>
          <p className="text-gray-600 leading-relaxed">
            See exactly what you've mastered with tangible results
          </p>
        </div>
      </div>
    </WorkshopStepWrapper>
  );
};

export default WorkshopLanding;